import { Stack, Typography, Box, CircularProgress, Paper, alpha, Fade, Link } from "@mui/material";
import Page from "../../../minimals/components/page";
import Iconify from "../../../minimals/components/iconify";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ContactSearchResult, ContactSummary, CompanySummary } from "./types/businessInsights";
import { SummaryCard } from "./SummaryCard";
import { SearchBar, SearchType } from "./SearchBar";
import { EmptyState } from "./EmptyState";
import { UserContext } from "../../contexts/UserContext";

const whitelistedUsers = [
  "agoldstein@madisoncres.com",
  "sdavidowitz@madisoncres.com",
  "bfishman@madisoncres.com",
  "mbecker@madisoncres.com",
  "swaldman@madisoncres.com",
  "sspanier@madisoncres.com"
];

export default function BusinessInsights() {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchType, setSearchType] = useState<SearchType>("Contacts");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ContactSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [selectedContactSummary, setSelectedContactSummary] = useState<ContactSummary | null>(null);
  const [selectedCompanySummary, setSelectedCompanySummary] = useState<CompanySummary | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressNextSearchRef = useRef(false);
  const hasSummary = !!(selectedContactSummary || selectedCompanySummary);
  const isWhiteListedUser = useMemo(() => {
    return (user?.email && whitelistedUsers.includes(user?.email?.toLowerCase())) || false;
  }, [user]);

  const [hasSearched, setHasSearched] = useState(false);

  const clearSearchPending = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSearching(false);
  }, []);

  const loadContactSummary = useCallback(async (contactId: string) => {
    setIsLoadingSummary(true);
    setSelectedContactSummary(null);
    setSelectedCompanySummary(null);
    try {
      const { data } = await axios.get<ContactSummary>("/api/businessInsights/contactSummary", {
        params: { dimDynamicsContactId: contactId }
      });
      setSelectedContactSummary(data ?? null);
      setHasSearched(true);
    } catch (error) {
      console.error("Error loading contact summary", error);
      setSelectedContactSummary(null);
      setHasSearched(true);
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  const loadContactSummaryByPhonebookId = useCallback(async (phonebookContactId: string) => {
    setIsLoadingSummary(true);
    setSelectedContactSummary(null);
    setSelectedCompanySummary(null);
    try {
      const { data } = await axios.get<ContactSummary>("/api/businessInsights/contactSummaryByPhonebookId", {
        params: { phonebookContactId }
      });
      setSelectedContactSummary(data ?? null);
      setHasSearched(true);
    } catch (error) {
      console.error("Error loading contact summary by phonebook id", error);
      setSelectedContactSummary(null);
      setHasSearched(true);
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  const loadCompanySummary = useCallback(async (companyId: string) => {
    setIsLoadingSummary(true);
    setSelectedContactSummary(null);
    setSelectedCompanySummary(null);
    try {
      const { data } = await axios.get<CompanySummary>("/api/businessInsights/companySummary", {
        params: { companyId }
      });
      setSelectedCompanySummary(data ?? null);
      setHasSearched(true);
    } catch (error) {
      console.error("Error loading company summary", error);
      setSelectedCompanySummary(null);
      setHasSearched(true);
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  useEffect(() => {
    const contactId = searchParams.get("contactId");
    const companyId = searchParams.get("companyId");
    const phonebookContactId = searchParams.get("phonebookContactId");

    if (contactId) {
      loadContactSummary(contactId);
    } else if (companyId) {
      loadCompanySummary(companyId);
    } else if (phonebookContactId) {
      loadContactSummaryByPhonebookId(phonebookContactId);
    }
  }, [searchParams, loadContactSummary, loadCompanySummary, loadContactSummaryByPhonebookId]);

  useEffect(() => {
    if (suppressNextSearchRef.current) {
      suppressNextSearchRef.current = false;
      setIsSearching(false);
      return;
    }

    const trimmed = searchTerm.trim();

    clearSearchPending();

    if (!trimmed) {
      setIsSearching(false);
      setSearchResults([]);
      return undefined;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { data } = await axios.get<ContactSearchResult[]>("/api/businessInsights/searchContacts", {
          params: { searchTerm: trimmed, type: searchType },
          signal: controller.signal
        });
        setSearchResults(data);
        setShowDropdown(true);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error searching", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 700);

    return () => {
      clearSearchPending();
    };
  }, [searchTerm, searchType, clearSearchPending]);

  const handleSelectContact = async (contact: ContactSearchResult) => {
    setShowDropdown(false);
    suppressNextSearchRef.current = true;
    setSearchTerm(contact.name ?? "");
    setHasSearched(false);

    if (!contact.recordId) return;

    if (contact.resultType === "Contact") {
      navigate(`?contactId=${contact.recordId}`);
    } else {
      navigate(`?companyId=${contact.recordId}`);
    }
  };

  const handleCompanyClick = useCallback(
    (companyId: number, companyName: string) => {
      setShowDropdown(false);
      suppressNextSearchRef.current = true;
      setSearchTerm(companyName);
      navigate(`?companyId=${companyId}`);
    },
    [navigate]
  );

  const handleSearchChange = (val: string) => {
    clearSearchPending();
    setSearchTerm(val);
    setShowDropdown(true);
  };

  const handleSearchTypeChange = (type: SearchType) => {
    setSearchType(type);
    setSearchTerm("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  if (!isWhiteListedUser) {
    return (
      <Page title="Business Insights" padding={{ xs: "12px", sm: "16px", md: "20px 24px", lg: "24px 32px" }}>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            maxWidth: 600,
            mx: "auto",
            mt: 8
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.error.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Iconify icon="eva:lock-fill" width={40} sx={{ color: theme.palette.error.main }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You do not have permission to access Business Insights.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Page>
    );
  }

  return (
    <Page title="Business Insights" padding={{ xs: "12px", sm: "16px", md: "20px 24px", lg: "24px 32px" }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Link href="/business-insights" underline="none" sx={{ cursor: "pointer" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20">
                <g fill="#fff" fillRule="evenodd">
                  <path d="M.25 1.609h17.272V0H.25zM4.061 19.73H5.67V8.042H4.06zM8.082 19.73h1.609V8.042H8.082zM12.103 19.73h1.609V8.042h-1.61zM15.486 5.63h-13.2a.803.803 0 1 1 0-1.61h13.2a.804.804 0 0 1 0 1.61"></path>
                </g>
              </svg>
            </Box>
          </Link>
          <Link href="/business-insights" underline="none" sx={{ cursor: "pointer" }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: -0.5,
                  mb: 0.25
                }}
              >
                Business Insights
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                MCRES Business Insights
              </Typography>
            </Box>
          </Link>
        </Stack>
      </Box>
      <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
        <SearchBar
          searchTerm={searchTerm}
          searchType={searchType}
          searchResults={searchResults}
          isSearching={isSearching}
          showDropdown={showDropdown}
          onSearchChange={handleSearchChange}
          onSearchTypeChange={handleSearchTypeChange}
          onFocus={() => setShowDropdown(true)}
          onClickAway={() => setShowDropdown(false)}
          onSelectContact={handleSelectContact}
          onCompanyClick={handleCompanyClick}
        />
      </Stack>

      {isLoadingSummary && (
        <Fade in>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.05)}`
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Box sx={{ position: "relative" }}>
                <CircularProgress size={40} thickness={5} />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Iconify icon="eva:bar-chart-2-fill" width={16} sx={{ color: theme.palette.primary.main }} />
                </Box>
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Loading insights...
              </Typography>
            </Stack>
          </Paper>
        </Fade>
      )}

      {!isLoadingSummary && hasSummary && (
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          {selectedContactSummary && (
            <SummaryCard kind="Contact" summary={selectedContactSummary} onCompanyClick={handleCompanyClick} />
          )}
          {selectedCompanySummary && <SummaryCard kind="Company" summary={selectedCompanySummary} />}
        </Box>
      )}

      {!isLoadingSummary && !hasSummary && <EmptyState hasSearched={hasSearched} />}
    </Page>
  );
}
